import { useEffect, useState } from 'react';
import {
  getPendingPetInvitations,
  acceptPetInvitation,
  declinePetInvitation,
  type PendingPetInvitation,
} from '@/api/pets';
import { usePets } from '../context/PetsContext';
import { useTheme } from '../context/ThemeContext';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';

export function PetInvitationsBanner() {
  const [invitations, setInvitations] = useState<PendingPetInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const { refreshPets } = usePets();
  const { colors } = useTheme();

  const fetchPending = async () => {
    try {
      setLoading(true);
      const data = await getPendingPetInvitations();
      // Defensive: API shape may vary; always set an array to avoid crash on invitations.length
      const list = Array.isArray(data?.invitations) ? data.invitations : [];
      setInvitations(list);
    } catch {
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleAccept = async (inv: PendingPetInvitation) => {
    setActingId(inv.petId);
    try {
      await acceptPetInvitation(inv.petId);
      setInvitations((prev) => prev.filter((i) => i.petId !== inv.petId));
      await refreshPets();
      toast.success(`${inv.petName} has been added to My Pets.`);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Failed to accept invitation'
      );
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async (inv: PendingPetInvitation) => {
    setActingId(inv.petId);
    try {
      await declinePetInvitation(inv.petId);
      setInvitations((prev) => prev.filter((i) => i.petId !== inv.petId));
      toast.success('Invitation declined.');
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Failed to decline invitation'
      );
    } finally {
      setActingId(null);
    }
  };

  const list = Array.isArray(invitations) ? invitations : [];
  if (loading || list.length === 0) return null;

  return (
    <div
      className="border-b px-4 py-3 flex flex-wrap items-center gap-3 justify-between"
      style={{
        backgroundColor: colors.surface,
        borderColor: colors.border,
      }}
    >
      <div className="flex flex-wrap items-center gap-4">
        {list.map((inv) => (
          <div
            key={inv.petId}
            className="flex flex-wrap items-center gap-2 sm:gap-3 py-1"
          >
            <p className="text-sm" style={{ color: colors.text }}>
              You&apos;ve been invited to be a Co-Parent / Caretaker for{' '}
              <strong>{inv.petName}</strong>
              {inv.inviterName && inv.inviterName !== 'Pet owner' && (
                <> by {inv.inviterName}</>
              )}
              .
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleAccept(inv)}
                disabled={actingId !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-opacity disabled:opacity-50"
                style={{
                  background:
                    'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)',
                }}
              >
                <Check className="w-4 h-4" />
                Accept
              </button>
              <button
                type="button"
                onClick={() => handleDecline(inv)}
                disabled={actingId !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-opacity disabled:opacity-50"
                style={{
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                <X className="w-4 h-4" />
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
